// Reports browser errors to /api/v2/reports/web (grouped server-side, shown in Admin > Diagnostics > Website)
// and catches render crashes so a broken page shows a way out instead of a blank screen.
import { Component, type ErrorInfo, type ReactNode } from "react";

/** Noise we can't act on: browser quirks, extensions, cross-origin scripts. */
const IGNORE = [/ResizeObserver loop/i, /^Script error\.?$/i, /extension:\/\//i, /^AbortError/i];
const MAX_PER_PAGE_LOAD = 10;
const sent = new Set<string>();

export function reportError(kind: "error" | "rejection" | "render", err: unknown) {
  try {
    const e = err instanceof Error ? err : new Error(typeof err === "string" ? err : JSON.stringify(err) ?? "Unknown error");
    const message = `${e.name && e.name !== "Error" ? `${e.name}: ` : ""}${e.message}`.slice(0, 500);
    const stack = (e.stack ?? "").slice(0, 4000);
    if (!message || IGNORE.some((re) => re.test(message) || re.test(stack))) return;
    const key = `${kind}|${message}`;
    if (sent.has(key) || sent.size >= MAX_PER_PAGE_LOAD) return;
    sent.add(key);
    void fetch("/api/v2/reports/web", {
      method: "POST", keepalive: true, credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "exoware" },
      body: JSON.stringify({ kind, message, stack, path: location.pathname }),
    }).catch(() => {});
  } catch { /* reporting must never throw */ }
}

let installed = false;
export function installErrorReporting() {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (ev) => reportError("error", ev.error ?? ev.message));
  window.addEventListener("unhandledrejection", (ev) => reportError("rejection", ev.reason));
}

/** Last-resort boundary around the whole app. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    const e = new Error(error.message);
    e.name = error.name;
    e.stack = `${error.stack ?? ""}\nComponent stack:${info.componentStack ?? ""}`;
    reportError("render", e);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center">
        <div className="max-w-sm">
          <div className="kicker mb-3 text-accent">// fault</div>
          <h1 className="text-2xl font-semibold">Something broke on this page</h1>
          <p className="mt-2 text-sm text-muted">It's been reported to us automatically. Reloading usually fixes it.</p>
          <div className="mt-6 flex justify-center gap-3">
            <button type="button" onClick={() => location.reload()} className="h-9 rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg">Reload</button>
            <a href="/" className="grid h-9 place-items-center rounded-lg border border-line px-4 text-sm">Home</a>
          </div>
        </div>
      </div>
    );
  }
}
